/**
 * 作者中心页面
 *
 * 显示用户的所有工作区卡片列表
 * 提供"创建工作区"功能
 * 显示工作区统计信息
 * 支持点击工作区卡片跳转到工作区详情页
 *
 * 架构说明:
 * - 使用HTTP REST API获取工作区列表（不依赖WebSocket）
 * - 使用 HttpOnly Cookie 认证
 * - WebSocket仅用于WorkspacePage的聊天功能
 */

import React, { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useAuthStore } from '../store/authStore';
import { WorkspaceCard } from '../components/workspace/WorkspaceCard';
import { CreateWorkspaceModal } from '../components/workspace/CreateWorkspaceModal';
import { EditWorkspaceModal } from '../components/workspace/EditWorkspaceModal';
import { AuthorCenterLayout } from '../components/layout/AuthorCenterLayout';
import { workspaceApi } from '../services/workspaceApi';
import type { Workspace as WorkspaceType } from '../types/workspace';
import type { Workspace as WorkspaceStoreType } from '../store/workspaceStore';
import { formatTokens } from '../utils/formatters';

/**
 * 作者中心页面组件
 */
export const AuthorCenter: React.FC = () => {
  // 路由跳转
  const navigate = useNavigate();

  // 认证状态
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const initialized = useAuthStore((state) => state.initialized);

  // 获取用户昵称
  const userNickname = useMemo(() => {
    return user?.nickname || user?.email?.split('@')[0] || '用户';
  }, [user]);

  // 跟踪是否正在导航（用于清理逻辑）
  const isNavigatingRef = useRef(false);

  // 组件卸载时的清理
  useEffect(() => {
    const handleBeforeUnload = () => {
      isNavigatingRef.current = true;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // 从store获取状态和方法
  const {
    workspaces,
    loading,
    error,
    fetchWorkspaces,
    setCurrentWorkspace,
    clearError
  } = useWorkspaceStore();

  // 模态框状态
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<WorkspaceType | null>(null);

  // 加载工作区列表（使用 Cookie 认证）
  useEffect(() => {
    // 只有在认证初始化完成且已登录时才获取数据
    if (initialized && isAuthenticated) {
      fetchWorkspaces();
    }
  }, [initialized, isAuthenticated, fetchWorkspaces]);

  // 处理创建工作区成功
  const handleCreateSuccess = useCallback((workspace: WorkspaceType) => {
    console.log('[AuthorCenter] 创建工作区成功，准备导航到 workspace.id:', workspace.id);

    // 先关闭弹窗
    setShowCreateModal(false);

    // 标记正在导航
    isNavigatingRef.current = true;

    // 导航到工作区详情页
    navigate(`/workspace/${workspace.id}`);
  }, [navigate]);

  // 处理工作区卡片点击
  const handleWorkspaceClick = useCallback((workspace: WorkspaceStoreType) => {
    setCurrentWorkspace(workspace);
    isNavigatingRef.current = true;
    navigate(`/workspace/${workspace.id}`);
  }, [setCurrentWorkspace, navigate]);

  // 处理错误重试（直接重新获取工作区列表）
  const handleRetry = useCallback(() => {
    clearError();
    fetchWorkspaces();
  }, [clearError, fetchWorkspaces]);

  // 处理编辑工作区
  const handleEditWorkspace = useCallback((workspace: WorkspaceType) => {
    setEditingWorkspace(workspace);
  }, []);

  // 处理删除工作区（复用编辑模态框）
  const handleDeleteWorkspace = useCallback((workspace: WorkspaceType) => {
    setEditingWorkspace(workspace);
  }, []);

  // Token 统计状态
  const [workspaceTokenStats, setWorkspaceTokenStats] = useState<Record<number, number>>({});
  const [tokenStatsLoading, setTokenStatsLoading] = useState(false);

  // 加载工作区 Token 统计
  useEffect(() => {
    if (workspaces.length === 0) {
      setWorkspaceTokenStats({});
      return;
    }

    let cancelled = false;
    setTokenStatsLoading(true);
    const statsMap: Record<number, number> = {};

    const loadStats = async () => {
      try {
        await Promise.all(
          workspaces.map(async (ws) => {
            try {
              const totalTokens = await workspaceApi.getTokenStats(undefined, ws.id);
              statsMap[ws.id] = totalTokens;
            } catch (error) {
              console.error(`Failed to load token stats for workspace ${ws.id}:`, error);
              statsMap[ws.id] = 0;
            }
          })
        );

        if (!cancelled) {
          setWorkspaceTokenStats(statsMap);
        }
      } finally {
        if (!cancelled) {
          setTokenStatsLoading(false);
        }
      }
    };

    loadStats();

    return () => {
      cancelled = true;
    };
  }, [workspaces.map(ws => ws.id).join(',')]); // 依赖工作区 ID 列表

  // 计算统计信息
  const stats = useMemo(() => {
    if (workspaces.length === 0) {
      return { totalWorkspaces: 0, totalWords: 0, totalTokens: 0 };
    }

    const stats = workspaces.reduce(
      (acc, ws) => {
        // 字数统计
        acc.totalWords += ws.created_words;
        // Token 统计：使用实时加载的 token 统计数据
        acc.totalTokens += workspaceTokenStats[ws.id] || 0;
        return acc;
      },
      { totalWords: 0, totalTokens: 0 }
    );

    return {
      totalWorkspaces: workspaces.length,
      totalWords: stats.totalWords,
      totalTokens: stats.totalTokens,
    };
  }, [workspaces, workspaceTokenStats]);

  // 等待认证初始化
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  // 未登录时不渲染（应该被 ProtectedRoute 重定向）
  if (!isAuthenticated) {
    return null;
  }

  return (
    <AuthorCenterLayout>
      <div className="h-full flex flex-col bg-stone-50">
        {/* 主内容区 */}
        <main className="flex-1 px-4 py-4">
          {/* 欢迎语 - 情感化标题 */}
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">你好，{userNickname}</h1>
            <p className="text-gray-600">欢迎回来，继续你的创作之旅</p>
          </div>

          {/* 我的工作区标题和创建按钮 */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">我的作品库</h2>
            {workspaces.length > 0 && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-gray-800 hover:bg-gray-700 text-white px-5 py-2.5 rounded-lg font-medium shadow-sm hover:shadow-lg active:scale-[0.98] transition-all duration-150"
              >
                <span className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  创建新工作区
                </span>
              </button>
            )}
          </div>

          {/* 加载状态 */}
          {loading && (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-gray-600"></div>
              <p className="mt-4 text-gray-600">加载中...</p>
            </div>
          )}

          {/* 错误状态 */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
              {error}
              <button
                onClick={handleRetry}
                className="ml-4 underline hover:text-red-800 transition-colors"
              >
                重试
              </button>
            </div>
          )}

          {/* 工作区卡片网格 */}
        {!loading && workspaces.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {workspaces.map(workspace => (
              <WorkspaceCard
                key={workspace.id}
                workspace={workspace as WorkspaceType}
                onClick={() => handleWorkspaceClick(workspace)}
                token="" // 不再需要 token
                tokenStats={workspaceTokenStats[workspace.id]}
                onEdit={handleEditWorkspace}
                onDelete={handleDeleteWorkspace}
              />
            ))}
          </div>
        )}

        {/* 空状态 - 情感化设计 */}
        {!loading && workspaces.length === 0 && !error && (
          <div className="text-center py-16">
            <div className="inline-block mb-6">
              <svg className="w-20 h-20 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">开启你的创作之旅</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">每一个伟大的故事都始于第一步。创建工作区，让灵感自由流动。</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-gray-800 hover:bg-gray-700 text-white px-8 py-3 rounded-lg font-medium shadow-md hover:shadow-lg active:scale-[0.98] transition-all duration-150"
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                创建新工作区
              </span>
            </button>
          </div>
        )}
      </main>

      {/* 底部统计栏 */}
      <footer className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="w-full flex justify-center gap-8 text-sm text-gray-600">
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            {stats.totalWorkspaces} 部作品
          </span>
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {stats.totalWords.toLocaleString()} 字
          </span>
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {tokenStatsLoading ? (
              <span className="inline-flex items-center gap-1">
                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                加载中...
              </span>
            ) : (
              formatTokens(stats.totalTokens) + ' Token'
            )}
          </span>
        </div>
      </footer>

      {/* 创建工作区对话框 */}
      <CreateWorkspaceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateSuccess={handleCreateSuccess}
      />

      {/* 编辑工作区对话框 */}
      <EditWorkspaceModal
        isOpen={!!editingWorkspace}
        onClose={() => setEditingWorkspace(null)}
        workspace={editingWorkspace}
        onUpdateSuccess={() => {
          fetchWorkspaces();
        }}
      />
      </div>
    </AuthorCenterLayout>
  );
};

export default AuthorCenter;
